import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { map, switchMap } from 'rxjs/operators';
import { OBJECT_STORES } from '../index-db';
import { combineLatest, from, Observable, of } from 'rxjs';
import { Organization } from '../user-account/user-organization.model';
import { DigitalBusinessCard } from '../dbc/dbc.model';

/**
 * Service to interact with IndexedDB
 * This service behaves as a wrapper around the NgxIndexedDBService,
 * providing a more readable and maintainable way to interact with IndexedDB.
 */
@Injectable({
    providedIn: 'root',
})
export class IndexedDbService {
    constructor(private ngxIndexedDBService: NgxIndexedDBService) {}

    /**
     * Checks if the IndexedDB database exists.
     * @returns Observable<boolean>
     */
    checkIfDBExists(): Observable<boolean> {
        if (!indexedDB.databases) {
            return of(false);
        }

        return from(indexedDB.databases()).pipe(
            switchMap((dbList) =>
                of(dbList.some((db) => db.name === 'uniqode-pwa-cache'))
            )
        );
    }

    /**
     * Retrieves all records from the specified object store.
     * @param storeName - The name of the object store.
     */
    getAll(storeName: string) {
        return this.checkIfDBExists().pipe(
            switchMap((dbExists) =>
                dbExists ? this.ngxIndexedDBService.getAll(storeName) : of([])
            )
        );
    }

    /**
     * Adds a new record to the specified object store.
     * @param storeName - The name of the object store.
     * @param value - The data to be added.
     */
    add(storeName: string, value: any) {
        return this.ngxIndexedDBService.add(storeName, value);
    }

    /**
     * Updates an existing record in the specified object store.
     * @param storeName - The name of the object store.
     * @param value - The data to be added.
     */
    update(storeName: string, value: any) {
        return this.ngxIndexedDBService.update(storeName, value);
    }

    /**
     * Retrieves a record by its ID from the specified object store.
     * @param storeName - The name of the object store.
     * @param id - The ID of the record to retrieve.
     */
    getByID(storeName: string, id: number) {
        return this.checkIfDBExists().pipe(
            switchMap((dbExists) =>
                dbExists
                    ? this.ngxIndexedDBService.getByID(storeName, id)
                    : of(null)
            )
        );
    }

    /**
     * Counts the number of records in the specified object store.
     * @param storeName - The name of the object store.
     */
    count(storeName: string) {
        return this.checkIfDBExists().pipe(
            switchMap((dbExists) =>
                dbExists ? this.ngxIndexedDBService.count(storeName) : of(0)
            )
        );
    }

    /**
     * Deletes the entire IndexedDB database.
     */
    deleteDatabase() {
        return this.checkIfDBExists().pipe(
            switchMap((dbExists) =>
                dbExists
                    ? this.ngxIndexedDBService.deleteDatabase()
                    : of(undefined)
            )
        );
    }

    /**
     * Clears all records from the specified object store and then adds new data.
     * @param storeName - The name of the object store.
     * @param data - The new data to be added after clearing the store.
     */
    clearAndAdd(storeName: string, data: any) {
        return this.checkIfDBExists().pipe(
            switchMap((dbExists) =>
                dbExists
                    ? this.ngxIndexedDBService
                          .clear(storeName)
                          .pipe(
                              switchMap(() =>
                                  this.ngxIndexedDBService.add(storeName, data)
                              )
                          )
                    : of(null)
            )
        );
    }

    /**
     * Retrieves the last accessed organization's cards.
     * If no organization is found, all cards are returned.
     */
    getLastAccessedOrgCards() {
        return combineLatest([
            this.ngxIndexedDBService.getAll(OBJECT_STORES.ORGANIZATIONS),
            this.ngxIndexedDBService.getAll(OBJECT_STORES.CARDS),
        ]).pipe(
            map(([orgs, cards]: [Organization[], DigitalBusinessCard[]]) => {
                const org = orgs.length > 0 ? orgs[0] : null;
                return org
                    ? cards.filter((card) => card.organization === org.id)
                    : [];
            })
        );
    }
}
